"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PreviewStage = PreviewStage;
exports.UsageMeter = UsageMeter;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const cn_1 = require("../lib/cn");
const RING = 'rounded-lg bg-background ring-2 ring-primary shadow-stage-lift';
function PreviewStage({ sharp, focus, children }) {
    const picture = (0, react_1.useRef)(null);
    const lifted = (0, react_1.useRef)(null);
    /**
     * The focus the lifted copy was last drawn for — so a NEW focus fades its copy in, and a
     * keystroke re-drawing the same one does not blink it.
     */
    const drawnFor = (0, react_1.useRef)(null);
    /**
     * ⚠️⚠️ **THE HORIZONTAL SLIDE IS GONE — 14 Oct 2026, the same day it came.** It existed for step
     * 3's team panel on the frame's far right edge, which fell off a 1080p screen. The product owner
     * then removed the panel itself — *"something that doesn't exist in the app"* — and step 3 now
     * cross-fades the picture to the people list instead, so nothing the stage lifts sits past the
     * edge and there is nothing left to slide to.
     */
    /* ⚠️ Every render, no dependency list: the picture changes with every keystroke in the form
       beside it, and the copy has to show the name being typed, not the name at mount. */
    (0, react_1.useLayoutEffect)(() => {
        const layer = lifted.current;
        const root = picture.current;
        if (layer === null || root === null)
            return;
        layer.replaceChildren();
        if (focus === null) {
            drawnFor.current = null;
            return;
        }
        const target = root.querySelector(focus);
        if (target === null)
            return;
        /* ⚠️ Offsets, not `getBoundingClientRect`: the frame is transformed, and a bounding rect is the
           projected, tilted box. Offsets are in the frame's own untransformed space, which is the
           space the copy is placed in. */
        let left = 0;
        let top = 0;
        let node = target;
        while (node !== null && node !== root) {
            left += node.offsetLeft;
            top += node.offsetTop;
            node = node.offsetParent;
        }
        const copy = target.cloneNode(true);
        copy.removeAttribute('id');
        copy.className = (0, cn_1.cn)(copy.className, RING);
        Object.assign(copy.style, {
            position: 'absolute',
            left: `${String(left)}px`,
            top: `${String(top)}px`,
            width: `${String(target.offsetWidth)}px`,
            height: `${String(target.offsetHeight)}px`,
            margin: '0',
        });
        layer.append(copy);
        /* ⚠️ A new part fades in with the picture's own cross-fade; the same part re-drawn on a
           keystroke does not. `animate` is absent in jsdom, and reduced motion gets no fade at all. */
        const fresh = drawnFor.current !== focus;
        drawnFor.current = focus;
        const still = typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (fresh && !still && typeof copy.animate === 'function') {
            copy.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 500, easing: 'ease-out' });
        }
    });
    return ((0, jsx_runtime_1.jsxs)("div", { "aria-hidden": "true", inert: true, className: "pointer-events-none relative h-full w-full overflow-hidden bg-stage select-none", children: [(0, jsx_runtime_1.jsx)("div", { className: "absolute inset-0", style: { perspective: '1900px' }, children: (0, jsx_runtime_1.jsx)("div", { className: "absolute top-[9%] left-[13%] h-[600px] w-[1080px] origin-[0%_40%]", style: { transform: 'rotateY(-17deg) rotateX(7deg) rotateZ(-1.5deg)' }, children: (0, jsx_runtime_1.jsx)("div", { className: "absolute top-0 left-0 h-[1200px] w-[2160px] origin-top-left", style: { transform: 'scale(0.5)', willChange: 'transform' }, children: (0, jsx_runtime_1.jsxs)("div", { className: "relative h-[600px] w-[1080px]", style: { zoom: 2 }, children: [(0, jsx_runtime_1.jsx)("div", { ref: picture, className: (0, cn_1.cn)('bg-sidebar text-foreground relative h-full w-full overflow-hidden rounded-xl border shadow-stage transition-[filter] duration-500', sharp ? 'blur-[0.2px]' : 'blur-[1.3px]'), children: children }), (0, jsx_runtime_1.jsx)("div", { className: (0, cn_1.cn)('absolute inset-0 z-10 rounded-xl bg-stage-scrim transition-opacity duration-500', sharp ? 'opacity-15 dark:opacity-20' : 'opacity-50 dark:opacity-45') }), (0, jsx_runtime_1.jsx)("div", { ref: lifted, className: "absolute inset-0 z-20" })] }) }) }) }), (0, jsx_runtime_1.jsx)("div", { className: "from-stage-edge absolute inset-y-0 left-0 w-[22%] bg-linear-to-r to-transparent" }), (0, jsx_runtime_1.jsx)("div", { className: "from-stage-edge-far absolute inset-y-0 right-0 w-[30%] bg-linear-to-l to-transparent" })] }));
}
/**
 * How much of an allowance is taken — *"4 / 10 hely foglalt"* over a thin bar, turning to the
 * warning colour when the count passes the limit. The onboarding mockup's seat counter.
 */
function UsageMeter({ text, used, limit, }) {
    const over = used > limit;
    return ((0, jsx_runtime_1.jsxs)("div", { className: "flex w-[148px] flex-col gap-2 pb-2", children: [(0, jsx_runtime_1.jsx)("span", { className: (0, cn_1.cn)('text-right text-[12px] font-medium tabular-nums', over ? 'text-[var(--status-warn-text)]' : 'text-muted-foreground'), children: text }), (0, jsx_runtime_1.jsx)("div", { className: "bg-muted h-1 overflow-hidden rounded-full", children: (0, jsx_runtime_1.jsx)("div", { className: (0, cn_1.cn)('h-full rounded-full transition-[width] duration-200', over ? 'bg-[var(--status-warn)]' : 'bg-primary'), style: { width: `${String(Math.min(100, limit === 0 ? 100 : (used / limit) * 100))}%` } }) })] }));
}
//# sourceMappingURL=preview-stage.js.map